import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useMutation } from 'react-query';
import { useForm } from 'react-hook-form';
import { 
  HiOutlineLocationMarker,
  HiOutlineCalculator,
  HiOutlineChartBar,
  HiOutlineArrowLeft,
  HiOutlineInformationCircle
} from 'react-icons/hi';
import { FaWhatsapp } from 'react-icons/fa';
import { api, apiUtils } from '../services/api';
import { useAuth } from '../context/AppContext';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const SolarParkParticipationChart = ({ project }) => {
  const totalInstalledPower = Number(project.total_power_installed || project.available_power || 0);
  const soldParticipationSales = (project.participation_sales || []).map((sale, index) => {
    const powerSold = Number(sale.power_sold_kwp || 0);
    const displayName = sale.is_public === false ? `Usuario ${index + 1}` : sale.name;
    return {
      ...sale,
      displayName,
      powerSold,
      percentage: totalInstalledPower > 0 ? (powerSold / totalInstalledPower) * 100 : 0,
      colorClass: index % 2 === 0 ? 'bg-blue-700' : 'bg-blue-300',
    };
  });
  const soldPower = soldParticipationSales.reduce((total, sale) => total + sale.powerSold, 0);
  const soldPercentage = totalInstalledPower > 0
    ? Math.min((soldPower / totalInstalledPower) * 100, 100)
    : 0;
  const availablePower = Math.max(Number(project.available_power || 0), 0);
  const availablePercentage = totalInstalledPower > 0
    ? Math.max(Math.min((availablePower / totalInstalledPower) * 100, 100), 0)
    : 0;
  const visualizationSegments = [];
  let cumulativePercentage = 0;

  soldParticipationSales.forEach((sale) => {
    const segmentPercentage = Math.max(sale.percentage, 0);
    if (segmentPercentage <= 0 || cumulativePercentage >= 100) return;

    const endPercentage = Math.min(cumulativePercentage + segmentPercentage, 100);
    visualizationSegments.push({
      type: 'sold',
      start: cumulativePercentage,
      end: endPercentage,
      colorClass: sale.colorClass,
      sale,
    });
    cumulativePercentage = endPercentage;
  });

  const getVisualizationSegment = (cellIndex) => {
    const cellMidpoint = cellIndex + 0.5;
    return visualizationSegments.find((segment) => (
      cellMidpoint > segment.start && cellMidpoint <= segment.end
    ));
  };

  return (
    <div className="card p-6">
      <h2 className="text-xl font-semibold mb-4">Participaciones del Parque Solar</h2>
      <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
        <div className="space-y-3">
          <div className="flex flex-wrap justify-between gap-2 text-sm text-gray-600">
            <span>Disponible: {apiUtils.formatPower(availablePower)}</span>
            <span>Capacidad total: {apiUtils.formatPower(totalInstalledPower)}</span>
          </div>

          <div className="relative">
            <div
              className="grid gap-1 bg-gray-100 p-4 rounded-lg"
              style={{ gridTemplateColumns: 'repeat(20, 1fr)' }}
            >
              {Array.from({ length: 100 }, (_, i) => {
                const segment = getVisualizationSegment(i);
                const isSoldPanel = segment?.type === 'sold';

                return (
                  <div
                    key={i}
                    className="group relative aspect-square"
                    style={{ animationDelay: `${i * 20}ms` }}
                  >
                    <div
                      className={`
                        h-full w-full rounded-sm transition-all duration-300 transform hover:scale-110
                        ${segment ? segment.colorClass : 'bg-gray-300'}
                      `}
                    />
                    {isSoldPanel && (
                      <div className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-56 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-left text-xs text-white shadow-lg group-hover:block">
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate font-semibold">{segment.sale.displayName}</div>
                            <div>{apiUtils.formatPower(segment.sale.powerSold)}</div>
                            <div>{apiUtils.formatNumber(segment.sale.percentage, 2)}% del parque solar</div>
                          </div>
                          {segment.sale.logo_url && (
                            <img
                              src={segment.sale.logo_url}
                              alt={`Logo ${segment.sale.displayName}`}
                              className="h-10 w-10 shrink-0 rounded bg-white object-contain p-1"
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg border">
                <div className="text-2xl font-bold text-gray-900">
                  {apiUtils.formatNumber(availablePercentage, 1)}%
                </div>
                <div className="text-xs text-gray-600 text-center">disponible</div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex items-center space-x-2">
              <div className="flex overflow-hidden rounded-sm">
                <div className="w-3 h-3 bg-blue-700"></div>
                <div className="w-3 h-3 bg-blue-300"></div>
              </div>
              <span className="text-gray-600">
                Participaciones vendidas ({apiUtils.formatPower(soldPower)} / {apiUtils.formatNumber(soldPercentage, 1)}%)
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-300 rounded-sm"></div>
              <span className="text-gray-600">Disponible</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const { userEmail, setUserEmail } = useAuth();
  const [simulationResult, setSimulationResult] = useState(null);
  const [showSimulator] = useState(false);
  const [limits, setLimits] = useState(null);

  // Función para obtener la imagen de perfil del proyecto
  const getProjectProfileImage = (projectData) => {
    if (projectData?.images?.length) {
      const featuredImage = projectData.images.find((image) => image.is_featured);
      return featuredImage?.image_url || projectData.images[0].image_url;
    }

    const projectName = typeof projectData === 'string' ? projectData : projectData?.name;
    if (!projectName) return '/images/backgrounds/cumunidades-proyectoparquearriba.jpg';
    
    const name = projectName.toLowerCase();
    
    // Mapeo de proyectos a la primera imagen de su galería
    const galleryFirstImages = {
      'corralito': '/images/projects/galeriaCorralito/DJI_0307.JPG',
      'luque': '/images/projects/galeriaLuque/0863b40a-aa50-4974-ad27-48d93ccfb917.jpg',
      'porteña': '/images/projects/galeriaPortena/dji_fly_20250626_171806_0451_1750996060217_photo (1).jpg',
      'villa del rosario': '/images/projects/galeriaVilladelrosario/Villa del rosario.png',
      'covilroos': '/images/projects/galeriaVilladelrosario/Villa del rosario.png',
      'rosario': '/images/projects/galeriaVilladelrosario/Villa del rosario.png',
    };
    
    // Buscar la imagen de la galería correspondiente
    for (const [keyword, imagePath] of Object.entries(galleryFirstImages)) {
      if (name.includes(keyword)) {
        return imagePath;
      }
    }
    
    // Fallback: usar imagen por defecto
    return '/images/backgrounds/cumunidades-proyectoparquearriba.jpg';
  };

  // Función para obtener la galería del proyecto
  const getProjectGallery = (projectData) => {
    if (projectData?.images?.length || projectData?.videos?.length) {
      return [
        ...(projectData.images || []).map((image) => ({
          type: 'image',
          src: image.image_url,
          alt: image.caption || projectData.name,
        })),
        ...(projectData.videos || []).map((video) => ({
          type: 'video',
          src: video.video_file_url || video.video || video.video_url,
          alt: video.title || projectData.name,
        })),
      ].filter((item) => item.src);
    }

    const projectName = typeof projectData === 'string' ? projectData : projectData?.name;
    if (!projectName) return [];
    
    const name = projectName.toLowerCase();
    let galleryFolder = '';
    
    if (name.includes('porteña')) {
      galleryFolder = 'galeriaPortena';
    } else if (name.includes('corralito')) {
      galleryFolder = 'galeriaCorralito';
    } else if (name.includes('covilroos') || name.includes('villa del rosario') || name.includes('rosario')) {
      galleryFolder = 'galeriaVilladelrosario';
    } else if (name.includes('luque')) {
      galleryFolder = 'galeriaLuque';
    } else if (name.includes('mercoop')) {
      galleryFolder = 'galeriaMercoop';
    }
    
    if (!galleryFolder) return [];
    
    // Definir las imágenes y videos para cada galería
    const galleries = {
      galeriaCorralito: [
        { type: 'image', src: '/images/projects/galeriaCorralito/DJI_0307.JPG', alt: 'Corralito 1' },
        { type: 'video', src: '/images/projects/galeriaCorralito/DJI_0308.MP4', alt: 'Video Corralito' },
        { type: 'image', src: '/images/projects/galeriaCorralito/DJI_0316.JPG', alt: 'Corralito 2' },
        { type: 'image', src: '/images/projects/galeriaCorralito/DJI_0317.JPG', alt: 'Corralito 3' },
      ],
      galeriaLuque: [
        { type: 'image', src: '/images/projects/galeriaLuque/0863b40a-aa50-4974-ad27-48d93ccfb917.jpg', alt: 'Luque 1' },
        { type: 'image', src: '/images/projects/galeriaLuque/51357B4B-6C0D-4ACD-BFD1-645151307592.jpg', alt: 'Luque 2' },
      ],
      galeriaMercoop: [],
      galeriaPortena: [
        { type: 'image', src: '/images/projects/galeriaPortena/dji_fly_20250626_171806_0451_1750996060217_photo (1).jpg', alt: 'Porteña 1' },
        { type: 'image', src: '/images/projects/galeriaPortena/dji_fly_20250626_172000_0452_1750996058055_photo.jpg', alt: 'Porteña 2' },
      ],
      galeriaVilladelrosario: [
        { type: 'image', src: '/images/projects/galeriaVilladelrosario/Villa del rosario.png', alt: 'Villa del Rosario' },
        { type: 'video', src: '/images/projects/galeriaVilladelrosario/DJI_0137.MP4', alt: 'Video Villa del Rosario' },
      ],
    };
    
    return galleries[galleryFolder] || [];
  };
  
  // Fetch project details
  const { data: project, isLoading, error } = useQuery(
    ['project', id],
    () => api.getProject(id)
  );

  // Scroll to top when component mounts or project changes
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, project]);
  
  // Fetch tariff categories
  const { data: tariffCategories } = useQuery(
    ['tariffCategories', project?.id],
    () => api.getTariffCategories(project.id),
    { enabled: Boolean(project?.id) }
  );
  
  // Simulation form
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      simulation_type: 'bill_coverage',
      monthly_bill_ars: '',
      tariff_category_id: '',
      bill_coverage_percentage: '50',
      number_of_panels: '',
      investment_amount_usd: '',
      user_email: userEmail,
      user_phone: '',
    }
  });
  
  const simulationType = watch('simulation_type');
  const monthlyBill = watch('monthly_bill_ars');
  const tariffCategoryId = watch('tariff_category_id');

  const {
    data: financialInfo,
    isLoading: financialInfoLoading,
    error: financialInfoError,
  } = useQuery(
    ['projectFinancialInfo', project?.id],
    () => api.getProjectFinancialInfo(project.id, userEmail || null),
    {
      enabled: Boolean(project?.id),
      retry: false,
    }
  );

  // Clear other fields when simulation type changes
  React.useEffect(() => {
    if (simulationType === 'bill_coverage') {
      setValue('number_of_panels', '');
      setValue('investment_amount_usd', '');
    } else if (simulationType === 'panels') {
      setValue('bill_coverage_percentage', '50');
      setValue('investment_amount_usd', '');
    } else if (simulationType === 'investment') {
      setValue('bill_coverage_percentage', '50');
      setValue('number_of_panels', '');
    }
  }, [simulationType, setValue]);

  // Calculate limits based on monthly bill
  React.useEffect(() => {
    const calculateLimits = async () => {
      if (!monthlyBill || !project || !tariffCategoryId) {
        setLimits(null);
        return;
      }

      if (monthlyBill <= 0) {
        setLimits(null);
        return;
      }

      try {
        const response = await api.calculateLimits({
          monthly_bill_ars: monthlyBill,
          project_id: project.id,
          tariff_category_id: tariffCategoryId
        });
        console.log('🔍 DEBUG - Limits response:', response.data);
        // Los datos están en response directamente, no en response.data
        const limitsData = response.data || response;
        console.log('🔍 DEBUG - Using limits data:', limitsData);
        setLimits(limitsData);
      } catch (error) {
        console.error('Error calculating limits:', error);
        setLimits(null);
      }
    };

    calculateLimits();
  }, [monthlyBill, project, tariffCategoryId]);

  // Calculate maximum panels based on bill coverage limits (100% max)
  const maxPanelsBasedOnBill = React.useMemo(() => {
    if (!limits?.max_panels_100_coverage) return null;
    return limits.max_panels_100_coverage;
  }, [limits]);

  const maxPanelsByTariffCategory = React.useMemo(() => {
    if (!limits?.max_panels_by_tariff_category || limits.max_panels_by_tariff_category <= 0) return null;
    return limits.max_panels_by_tariff_category;
  }, [limits]);

  const maxPanelsAllowedByCalculation = React.useMemo(() => {
    if (!limits?.max_panels_allowed) return null;
    return limits.max_panels_allowed;
  }, [limits]);

  // Calculate suggested maximum panels based on project availability
  const maxAvailablePanels = React.useMemo(() => {
    if (!project) return null;
    
    const panelPowerKw = project.panel_power_wp / 1000; // Convert Wp to kW
    const maxPanels = Math.floor(project.available_power / panelPowerKw);
    
    return Math.max(1, maxPanels); // At least 1 panel
  }, [project]);

  // Use the most restrictive limit: either bill-based or project capacity
  const maxPanels = React.useMemo(() => {
    if (!maxPanelsAllowedByCalculation && !maxAvailablePanels) return null;
    if (!maxPanelsAllowedByCalculation) return maxAvailablePanels;
    if (!maxAvailablePanels) return maxPanelsAllowedByCalculation;
    return Math.min(maxPanelsAllowedByCalculation, maxAvailablePanels);
  }, [maxPanelsAllowedByCalculation, maxAvailablePanels]);

  const isLimitedByTariffCategory = Boolean(
    maxPanelsByTariffCategory &&
    maxPanelsBasedOnBill &&
    maxPanelsByTariffCategory < maxPanelsBasedOnBill
  );
  const tariffLimitCategoryName = limits?.tariff_category_name || 'seleccionada';
  const panelLimitValidationMessage = isLimitedByTariffCategory
    ? `Máximo ${maxPanels} paneles (tu categoría tarifaria permite comprar hasta ${maxPanelsByTariffCategory} paneles)`
    : `Máximo ${maxPanels} paneles (el ahorro no puede superar el 100% de tu factura mensual)`;
  const panelLimitAlertMessage = isLimitedByTariffCategory
    ? `⚠️Máximo ${maxPanels} paneles para no superar el límite de compra de categoría tarifaria ${tariffLimitCategoryName}`
    : `⚠️Máximo ${maxPanels} paneles para no superar el 100% de cobertura de tu factura`;
  const investmentLimitHelp = isLimitedByTariffCategory
    ? `⚠️ Limitado por tu categoría tarifaria (categoría tarifaria "${tariffLimitCategoryName}" permite comprar hasta ${maxPanelsByTariffCategory} paneles)`
    : `💡 Máximo $${apiUtils.formatNumber(limits?.max_investment_usd || 0, 0)} USD para no superar el 100% de cobertura de tu factura`;
  
  // Create simulation mutation
  const createSimulationMutation = useMutation(api.createSimulation, {
    onSuccess: (data) => {
      setSimulationResult(data);
      if (data.simulation.user_email && data.simulation.user_email !== userEmail) {
        setUserEmail(data.simulation.user_email);
      }
      toast.success('Simulación creada exitosamente');
    },
    onError: (error) => {
      const errorMessage = apiUtils.extractErrorMessage(error);
      toast.error(errorMessage);
    },
  });
  
  const onSubmit = (data) => {
    const payload = {
      project_id: project.id,
      monthly_bill_ars: parseFloat(data.monthly_bill_ars),
      tariff_category_id: parseInt(data.tariff_category_id),
      user_email: data.user_email,
      user_phone: data.user_phone,
    };
    
    // Add the specific simulation parameter
    if (simulationType === 'bill_coverage') {
      payload.bill_coverage_percentage = parseFloat(data.bill_coverage_percentage);
    } else if (simulationType === 'panels') {
      payload.number_of_panels = parseInt(data.number_of_panels);
    } else if (simulationType === 'investment') {
      payload.investment_amount_usd = parseFloat(data.investment_amount_usd);
    }
    
    createSimulationMutation.mutate(payload);
  };
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }
  
  if (error || !project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Proyecto no encontrado</p>
          <Link to="/" className="btn btn-primary">
            Volver al Inicio
          </Link>
        </div>
      </div>
    );
  }
  
  const getStatusColor = (status) => {
    const colors = {
      development: 'bg-blue-100 text-blue-800',
      funding: 'bg-green-100 text-green-800',
      construction: 'bg-yellow-100 text-yellow-800',
      operational: 'bg-green-100 text-green-800',
      completed: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };
  
  const getStatusLabel = (status) => {
    const labels = {
      development: 'En Desarrollo',
      funding: 'En Financiamiento',
      construction: 'En Construcción',
      operational: 'Operativo',
      completed: 'Completado',
    };
    return labels[status] || status;
  };

  const formatPrice = (value, decimals = 2) => apiUtils.formatNumber(Number(value || 0), decimals);
  const basePanelPrice = financialInfo
    ? financialInfo.price_per_panel_usd || (financialInfo.price_per_wp_usd * financialInfo.panel_power_wp)
    : 0;
  const promotionalRanges = financialInfo?.sale_pricing_mode === 'promotional'
    ? [...(financialInfo.promotional_price_ranges || [])].sort((a, b) => Number(a.min_panels) - Number(b.min_panels))
    : [];
  const getRangePriceColor = (index) => {
    if (index === 0) return 'text-red-600';
    if (index === 1) return 'text-yellow-600';
    return 'text-green-600';
  };
  const rangeLabel = (range) => (
    range.max_panels ? `${range.min_panels}-${range.max_panels} paneles` : `+${range.min_panels} paneles`
  );
  
  return (
    <div className="min-h-screen relative">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src={getProjectProfileImage(project)}
          alt={`${project.name} - Proyecto Solar`}
          className="w-full h-full object-cover object-center"
          onError={(e) => {
            e.target.src = '/images/backgrounds/cumunidades-proyectoparquearriba.jpg';
          }}
        />
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-primary-900/80 to-primary-600/70"></div>
      </div>

      <div className="container-xl section-padding relative z-10 py-8">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center text-white hover:text-gray-200 mb-6 transition-colors duration-300"
        >
          <HiOutlineArrowLeft className="w-5 h-5 mr-2" />
          Volver al Inicio
        </Link>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Project Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Header */}
            <div className="card overflow-hidden">
              <div className="aspect-[3/1] w-full bg-gradient-to-r from-primary-100 to-secondary-100">
                {project.banner_url ? (
                  <img
                    src={project.banner_url}
                    alt={`Banner ${project.name}`}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center px-6 text-center">
                    <h1 className="text-2xl font-bold text-primary-800 sm:text-3xl">
                      {project.name}
                    </h1>
                  </div>
                )}
              </div>

              <div className="p-6">
                <div className="mb-4">
                  <span className={`inline-flex px-4 py-2 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                </div>

                <p className="text-gray-700 leading-relaxed mb-4">
                  {project.description}
                </p>
                
                <div className="flex items-center text-gray-600">
                  <HiOutlineLocationMarker className="w-5 h-5 mr-2" />
                  <span>{project.location}</span>
                </div>
              </div>
            </div>
            
            {/* Project Images */}
            {(() => {
              const gallery = getProjectGallery(project);
              
              if (gallery.length === 0) return null;
              
              return (
                <div className="card p-6">
                  <h2 className="text-xl font-semibold mb-4">Galería del Proyecto</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {gallery.map((item, index) => (
                      <div key={`gallery-${index}`} className="relative aspect-video rounded-lg overflow-hidden bg-gray-900">
                        {item.type === 'image' ? (
                          <img
                            src={item.src}
                            alt={item.alt}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <video
                            controls
                            className="w-full h-full object-cover"
                            preload="metadata"
                          >
                            <source src={item.src} type="video/mp4" />
                            Tu navegador no soporta video HTML5.
                          </video>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}
            
            {/* Technical Specifications */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-4">Especificaciones Técnicas</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-600">Potencia Total Proyectada</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {apiUtils.formatPower(project.total_power_projected)}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Potencia Instalada</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {apiUtils.formatPower(project.total_power_installed)}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Potencia Disponible</label>
                  <p className="text-lg font-semibold text-primary-600">
                    {apiUtils.formatPower(project.available_power)}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Disponibilidad</label>
                  <p className="text-lg font-semibold text-accent-600">
                    {apiUtils.formatNumber(project.available_power_percentage, 1)}%
                  </p>
                </div>
                
                {project.expected_annual_generation && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">Generación Anual Esperada</label>
                    <p className="text-lg font-semibold text-gray-900">
                      {apiUtils.formatEnergy(project.expected_annual_generation)}
                    </p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium text-gray-600">Potencia por Panel</label>
                  <p className="text-lg font-semibold text-gray-900">
                    {apiUtils.formatNumber(project.panel_power_wp)} Wp
                  </p>
                </div>
              </div>
            </div>

            <SolarParkParticipationChart project={project} />
            
            {/* Financial Information */}
            <div className="card p-6 relative">
              <h2 className="text-xl font-semibold mb-4">Información Financiera</h2>
              
              {financialInfoLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="medium" />
                </div>
              ) : financialInfoError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  No se pudo cargar la información financiera.
                </div>
              ) : financialInfo ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {promotionalRanges.length > 0 ? (
                    <>
                      <div className="sm:col-span-2">
                        <label className="text-sm font-medium text-gray-600 mb-3 block">Precio por Wp según Cantidad</label>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {promotionalRanges.map((range, index) => (
                              <div key={range.id} className="text-center p-3 bg-white rounded border">
                                <div className="text-xs font-medium text-gray-500 mb-1">Rango {index + 1}</div>
                                <div className="text-sm font-semibold text-gray-700">{rangeLabel(range)}</div>
                                <div className={`text-lg font-bold ${getRangePriceColor(index)}`}>
                                  ${formatPrice(range.price_per_wp_usd)} USD
                                </div>
                                <div className="text-xs text-gray-500">por Wp</div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 text-xs text-gray-600 text-center">
                            Precios calculados con paneles de {apiUtils.formatNumber(financialInfo.panel_power_wp)} Wp
                          </div>
                        </div>
                      </div>

                      <div className="sm:col-span-2">
                        <label className="text-sm font-medium text-gray-600 mb-3 block">Precio por Panel según Cantidad</label>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {promotionalRanges.map((range, index) => (
                              <div key={range.id} className="text-center p-3 bg-white rounded border">
                                <div className="text-xs font-medium text-gray-500 mb-1">Rango {index + 1}</div>
                                <div className="text-sm font-semibold text-gray-700">{rangeLabel(range)}</div>
                                <div className={`text-lg font-bold ${getRangePriceColor(index)}`}>
                                  ${formatPrice(range.price_per_panel_usd, 0)} USD
                                </div>
                                <div className="text-xs text-gray-500">por panel</div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 text-xs text-gray-600 text-center">
                            Los precios se aplican según la cantidad total de paneles.
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Precio por Wp</label>
                        <p className="text-lg font-semibold text-green-600">
                          ${formatPrice(financialInfo.price_per_wp_usd)} USD
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-600">Precio por Panel</label>
                        <p className="text-lg font-semibold text-green-600">
                          ${formatPrice(basePanelPrice, 0)} USD
                        </p>
                      </div>
                    </>
                  )}

                </div>
              ) : null}
            </div>
            
            {/* Project Owners */}
            {project.owners && (
              <div className="card p-6">
                <h2 className="text-xl font-semibold mb-4">Propietarios del Proyecto</h2>
                <p className="text-gray-700">{project.owners}</p>
              </div>
            )}
          </div>
          
          {/* Investment Simulator */}
          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-8 relative">
              <div className="flex items-center space-x-2 mb-4">
                <HiOutlineCalculator className="w-6 h-6 text-primary-600" />
                <h2 className="text-xl font-semibold">Simulador de Inversión</h2>
              </div>
              
              <>
                {!showSimulator ? (
                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    Calcula el retorno de tu inversión en este proyecto
                  </p>
                  <button
                    onClick={() => {
                      window.location.href = `/simular/${project.slug || project.id}`;
                    }}
                    className="btn btn-primary w-full text-center"
                  >
                    Simular Inversión
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* User Email */}
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      {...register('user_email', { 
                        required: 'Email es requerido',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Email no válido'
                        }
                      })}
                      placeholder="tu@email.com"
                      className={`input ${errors.user_email ? 'input-error' : ''}`}
                    />
                    {errors.user_email && (
                      <p className="form-error">{errors.user_email.message}</p>
                    )}
                  </div>
                  
                  {/* User Phone */}
                  <div>
                    <label className="form-label">Teléfono *</label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                        +54
                      </span>
                      <input
                        type="tel"
                        {...register('user_phone', { 
                          required: 'Teléfono es requerido',
                          minLength: { value: 8, message: 'Mínimo 8 dígitos' }
                        })}
                        placeholder="11 1234-5678"
                        className={`input rounded-l-none ${errors.user_phone ? 'input-error' : ''}`}
                      />
                    </div>
                    {errors.user_phone && (
                      <p className="form-error">{errors.user_phone.message}</p>
                    )}
                    <p className="form-help">Número sin el código de país</p>
                  </div>
                  
                  {/* Monthly Bill */}
                  <div>
                    <label className="form-label">Factura Mensual (Impuestos incluidos) *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                      <input
                        type="number"
                        {...register('monthly_bill_ars', { 
                          required: 'Este campo es requerido',
                          min: { value: 1, message: 'Debe ser mayor a $0' }
                        })}
                        placeholder="50000"
                        className={`input pl-8 ${errors.monthly_bill_ars ? 'input-error' : ''}`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">ARS</span>
                    </div>
                    {errors.monthly_bill_ars && (
                      <p className="form-error">{errors.monthly_bill_ars.message}</p>
                    )}
                    <p className="form-help">Monto total de tu factura de luz mensual</p>
                  </div>
                  
                  {/* Tariff Category */}
                  <div>
                    <label className="form-label">Categoría Tarifaria *</label>
                    <select
                      {...register('tariff_category_id', { 
                        required: 'Selecciona una categoría' 
                      })}
                      className={`input ${errors.tariff_category_id ? 'input-error' : ''}`}
                    >
                      <option value="">Seleccionar...</option>
                      {tariffCategories?.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name} ({category.code})
                        </option>
                      ))}
                    </select>
                    {errors.tariff_category_id && (
                      <p className="form-error">{errors.tariff_category_id.message}</p>
                    )}
                  </div>
                  
                  {/* Simulation Type */}
                  <div>
                    <label className="form-label">Tipo de Simulación</label>
                    <select
                      {...register('simulation_type')}
                      className="input"
                    >
                      <option value="bill_coverage">Cobertura de Factura</option>
                      <option value="panels">Número de Paneles</option>
                      <option value="investment">Monto de Inversión</option>
                    </select>
                  </div>
                  
                  {/* Dynamic Input Based on Simulation Type */}
                  {simulationType === 'bill_coverage' && (
                    <div>
                      <label className="form-label">Porcentaje de Cobertura de Factura (%)</label>
                      <input
                        type="number"
                        {...register('bill_coverage_percentage', {
                          min: { value: 1, message: 'Mínimo 1%' },
                          max: { value: 100, message: 'Máximo 100%' }
                        })}
                        min="1"
                        max="100"
                        className={`input ${errors.bill_coverage_percentage ? 'input-error' : ''}`}
                      />
                      {errors.bill_coverage_percentage && (
                        <p className="form-error">{errors.bill_coverage_percentage.message}</p>
                      )}
                      <p className="form-help">¿Qué porcentaje de tu factura quieres cubrir con energía solar?</p>
                    </div>
                  )}
                  
                  {simulationType === 'panels' && (
                    <div>
                      <label className="form-label">
                        Cantidad de Paneles
                        {maxPanels && (
                          <span className="text-sm text-gray-500 font-normal">
                            {' '}(máximo {maxPanels} paneles)
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        {...register('number_of_panels', {
                          required: 'Número de paneles es requerido',
                          min: { value: 1, message: 'Mínimo 1 panel' },
                          max: maxPanels ? { 
                            value: maxPanels, 
                            message: panelLimitValidationMessage 
                          } : undefined
                        })}
                        min="1"
                        max={maxPanels || undefined}
                        className={`input ${errors.number_of_panels ? 'input-error' : ''}`}
                      />
                      {errors.number_of_panels && (
                        <p className="form-error">{errors.number_of_panels.message}</p>
                      )}
                      {maxPanels && (
                        <p className="text-xs text-gray-600 mt-1">
                          {panelLimitAlertMessage}
                        </p>
                      )}
                    </div>
                  )}
                  
                  {simulationType === 'investment' && (
                    <div>
                      <label className="form-label">
                        Monto de Inversión (USD)
                        {limits?.max_investment_usd && (
                          <span className="text-sm text-gray-500 font-normal">
                            {' '}(máximo ${apiUtils.formatNumber(limits.max_investment_usd, 0)} USD)
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        {...register('investment_amount_usd', {
                          required: 'Monto de inversión es requerido',
                          min: { value: 1, message: 'Mínimo $1 USD' },
                          max: limits?.max_investment_usd ? { 
                            value: limits.max_investment_usd, 
                            message: `Máximo $${apiUtils.formatNumber(limits.max_investment_usd, 0)} USD (no puede superar el 100% de cobertura de tu factura)` 
                          } : undefined,
                          setValueAs: (value) => String(value || '').replace(/\D/g, ''),
                          onChange: (event) => {
                            event.target.value = event.target.value.replace(/\D/g, '');
                          }
                        })}
                        onInput={(event) => {
                          event.currentTarget.value = event.currentTarget.value.replace(/\D/g, '');
                        }}
                        className={`input ${errors.investment_amount_usd ? 'input-error' : ''}`}
                      />
                      {errors.investment_amount_usd && (
                        <p className="form-error">{errors.investment_amount_usd.message}</p>
                      )}
                      {limits?.max_investment_usd && (
                        <p className="text-xs text-gray-600 mt-1">
                          {investmentLimitHelp}
                        </p>
                      )}
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={createSimulationMutation.isLoading}
                    className="btn btn-primary w-full"
                  >
                    {createSimulationMutation.isLoading ? (
                      <LoadingSpinner size="small" color="white" />
                    ) : (
                      'Calcular Simulación'
                    )}
                  </button>
                </form>
              )}
              
              {/* Simulation Results */}
              {simulationResult && (
                <SimulationResults 
                  result={simulationResult} 
                  onReset={() => setSimulationResult(null)}
                  project={{ ...project, ...(financialInfo || {}) }}
                />
              )}
              </>
            </div>
          </div>
        </div>
        
      </div>
      
    </div>
  );
};

// Function to create WhatsApp message
const createWhatsAppMessage = (project, simulation, userEmail, userPhone) => {
  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('es-AR', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    }).format(num);
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0';
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getSimulationTypeLabel = (type) => {
    const labels = {
      'bill_coverage': 'Cobertura de Factura',
      'panels': 'Número de Paneles',
      'investment': 'Monto de Inversión'
    };
    return labels[type] || type;
  };

  return `🌞 *CONSULTA SIMULACIÓN SOLAR* 🌞

📋 *PARÁMETROS DE SIMULACIÓN*
• Proyecto: ${project.name}
• Ubicación: ${project.location}
• Tipo de simulación: ${getSimulationTypeLabel(simulation.simulation_type)}
• Factura mensual: ${formatCurrency(simulation.monthly_bill_ars)}
${simulation.bill_coverage_percentage ? `• Cobertura deseada: ${formatNumber(simulation.bill_coverage_percentage, 1)}%` : ''}
${simulation.number_of_panels ? `• Cantidad de paneles: ${simulation.number_of_panels}` : ''}
${simulation.investment_amount_usd ? `• Monto de inversión: $${formatNumber(simulation.investment_amount_usd)} USD` : ''}

📊 *RESULTADOS DE LA SIMULACIÓN*
💰 Inversión Total: $${formatNumber(simulation.total_investment_usd)} USD
⚡ Potencia Instalada: ${formatNumber(simulation.installed_power_kw, 2)} kW
📈 Generación Mensual: ${formatNumber(simulation.monthly_generation_kwh)} kWh
💵 Ahorro Mensual: ${formatCurrency(simulation.monthly_savings_ars)}
💎 Ahorro Anual (USD): $${formatNumber(simulation.annual_savings_usd, 0)}
📊 Retorno Anual en USD: ${formatNumber(simulation.irr_annual_usd, 1)}%
⏰ Período de Retorno: ${formatNumber(simulation.payback_period_years, 1)} años
🎯 Cobertura Lograda: ${formatNumber(simulation.bill_coverage_achieved, 1)}%

👤 *DATOS DE CONTACTO*
📧 Email: ${userEmail}
📱 Teléfono: +54${userPhone}

Quisiera recibir asesoramiento comercial sobre esta simulación. ¡Gracias! 🙌`;
};

// Simulation Results Component
const SimulationResults = ({ result, onReset, project }) => {
  const { simulation, capacity_check } = result;
  const commercialWhatsApp = project.commercial_whatsapp || simulation.project_commercial_whatsapp;
  
  // Debug: Check if WhatsApp number is available
  console.log('🔍 DEBUG - SimulationResults rendered');
  console.log('🔍 Project data:', project);
  console.log('🔍 Project WhatsApp:', commercialWhatsApp);
  console.log('🔍 Should show button:', !!commercialWhatsApp);
  console.log('🔍 Project keys:', Object.keys(project));
  
  const handleWhatsAppContact = () => {
    if (!commercialWhatsApp) {
      toast.error('No hay número de WhatsApp configurado para este proyecto');
      return;
    }
    
    const message = createWhatsAppMessage(project, simulation, simulation.user_email, simulation.user_phone);
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${commercialWhatsApp}&text=${encodedMessage}`;
    
    window.open(whatsappUrl, '_blank');
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 p-4 bg-primary-50 rounded-lg border border-primary-200"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-primary-900">
          Resultados de la Simulación
        </h3>
        <button
          onClick={onReset}
          className="text-primary-600 hover:text-primary-700 text-sm"
        >
          Nueva Simulación
        </button>
      </div>
      
      {/* Capacity Warning */}
      {!capacity_check.has_capacity && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 rounded-lg">
          <div className="flex items-center space-x-2">
            <HiOutlineInformationCircle className="w-5 h-5 text-yellow-600" />
            <p className="text-sm text-yellow-800">
              La potencia requerida excede la disponibilidad del proyecto
            </p>
          </div>
        </div>
      )}
      
      <div className="space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">Inversión Total:</span>
          <span className="font-semibold">
            ${apiUtils.formatNumber(simulation.total_investment_usd)} USD
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Potencia Instalada:</span>
          <span className="font-semibold">
            {apiUtils.formatPower(simulation.installed_power_kw)}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Generación Mensual:</span>
          <span className="font-semibold">
            {apiUtils.formatNumber(simulation.monthly_generation_kwh)} kWh
          </span>
        </div>
        
        <div className="flex justify-between gap-3">
          <span className="text-gray-600">Ahorro Mensual (ARS):</span>
          <span className="min-w-0 break-words text-right font-semibold text-green-600">
            {apiUtils.formatCurrency(simulation.monthly_savings_ars)}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Ahorro Anual (USD):</span>
          <span className="font-semibold text-purple-600">
            ${apiUtils.formatNumber(simulation.annual_savings_usd, 0)}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Retorno Anual en USD:</span>
          <span className="font-semibold text-green-600">
            {apiUtils.formatNumber(simulation.irr_annual_usd, 1)}%
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Periodo de Retorno:</span>
          <span className="font-semibold">
            {apiUtils.formatNumber(simulation.payback_period_years, 1)} años
          </span>
        </div>
        
        <div className="flex justify-between">
          <span className="text-gray-600">Cobertura de Factura Lograda:</span>
          <span className="font-semibold text-primary-600">
            {apiUtils.formatNumber(simulation.bill_coverage_achieved, 1)}%
          </span>
        </div>
      </div>
      
      <div className="mt-4 pt-4 border-t border-primary-200 space-y-3">
        <div className="flex space-x-2">
          <Link
            to="/mis-simulaciones"
            className="btn btn-outline flex-1 text-sm"
          >
            <HiOutlineChartBar className="w-4 h-4 mr-2" />
            Ver Todas mis Simulaciones
          </Link>
          <button
            onClick={onReset}
            className="btn btn-outline flex-1 text-sm"
          >
            Nueva Simulación
          </button>
        </div>
        
        {commercialWhatsApp && (
          <button
            onClick={handleWhatsAppContact}
            className="btn w-full text-sm text-white bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600 transition-colors flex items-center justify-center"
          >
            <FaWhatsapp className="w-5 h-5 mr-2" />
            Asesor Comercial
          </button>
        )}
        
        {/* Debug: Always show this button for testing */}
        <button
          onClick={() => {
            console.log('🔧 Debug button clicked');
            console.log('🔧 project.commercial_whatsapp:', commercialWhatsApp);
            console.log('🔧 Boolean check:', !!commercialWhatsApp);
          }}
          className="btn w-full text-sm bg-red-500 text-white"
        >
          🔧 DEBUG: WhatsApp = {commercialWhatsApp || 'undefined'}
        </button>
        
        {/* Debug: Show conditional result */}
        <div className="text-xs bg-yellow-100 p-2 rounded">
          <strong>DEBUG INFO:</strong><br/>
          WhatsApp: {commercialWhatsApp || 'NO VALUE'}<br/>
          Show button: {commercialWhatsApp ? 'YES' : 'NO'}<br/>
          Type: {typeof commercialWhatsApp}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectDetailPage;